import { AnyAction } from 'redux';

import { INTERACTION_DRAW_FEATURES } from '@tmapy/mapcore';

import type { OrdersPublishsetEditResponse } from '../../openapi/types';
import { OrderTypeEnum } from '../../openapi/types/OrderTypeEnum';
import { OrderSelectionTypesEnum } from '../../openapi/types/OrderSelectionTypesEnum';
import { OriginEnum } from '../../openapi/types/OriginEnum';

import { ORDER_IS_PUBLIC, QUERY_STATE } from '../../constants';

import type {
  PublishsetFormatPayload,
  PublishsetSetAllFormatsPayload,
  PublishsetRemoveAllFormatsPayload,
  AttachmentProps,
} from '../actions/newOrder';
import * as con from '../constants';

const newOrderState = {
  isDirty: false,
  fieldErrors: null,
  id: '',
  cadasters: [] as any[],
  cities: [] as any[],
  orps: [] as any[],
  regions: [] as any[],
  momcs: [] as any[],
  order_publishsets: [] as OrdersPublishsetEditResponse[],
  reason: '',
  handling_fee: false,
  note: '',
  origin: OriginEnum.EVYDEJ as OriginEnum | null,
  queryState: QUERY_STATE.EMPTY,
  agreement: false,
  isRequirementPublishset: false,
  isRequirementValidRegion: false,
  isRequirementAgreement: false,
  isRequirementDate: false,
  attachments: [] as AttachmentProps[],
  order_type: OrderTypeEnum.STATE as OrderTypeEnum,
  order_data_state_valid_at: null as Date | null, // TODO A timestamp should not be used, but only a date without a time.
  order_data_changes_from: null as Date | null, // TODO A timestamp should not be used, but only a date without a time.
  selection_type: OrderSelectionTypesEnum.CUSTOM as OrderSelectionTypesEnum,
  areaType: '',
  isOverLimitArea: false,
  is_public: ORDER_IS_PUBLIC.TRUE,
  from_primary_db: false,
  dateValidityMin: null as Date | null,
  dateValidityMax: null as Date | null,
};

export const newOrder = (state = newOrderState, action: AnyAction): typeof newOrderState => {
  switch (action.type) {
    case con.NEW_ORDER_CLEAR: {
      return newOrderState;
    }

    case con.NEW_ORDER_SET_CADASTER:
      return {
        ...state,
        isDirty: true,
        cadasters: [...action.cadasters],
        isRequirementValidRegion: state.isRequirementValidRegion && !action.cadasters.length,
      };
    case con.NEW_ORDER_SET_CITIES:
      return {
        ...state,
        isDirty: true,
        cities: [...action.cities],
        isRequirementValidRegion: state.isRequirementValidRegion && !action.cities.length,
      };
    case con.NEW_ORDER_SET_ORPS:
      return {
        ...state,
        isDirty: true,
        orps: [...action.orps],
        isRequirementValidRegion: state.isRequirementValidRegion && !action.orps.length,
      };
    case con.NEW_ORDER_SET_REGIONS:
      return {
        ...state,
        isDirty: true,
        regions: [...action.regions],
        isRequirementValidRegion: state.isRequirementValidRegion && !action.regions.length,
      };
    case con.NEW_ORDER_SET_MOMCS:
      return {
        ...state,
        isDirty: true,
        momcs: [...action.momcs],
        isRequirementValidRegion: state.isRequirementValidRegion && !action.momcs.length,
      };
    case INTERACTION_DRAW_FEATURES:
      return {
        ...state,
        isRequirementValidRegion: false,
      };

    case con.NEW_ORDER_PUBLISHSET_ADD_FORMAT: {
      const { publishsetUrl, formatUrl } = action.payload as PublishsetFormatPayload;
      let found = false;
      let order_publishsets = state.order_publishsets.map((publishset) => {
        if (publishset.publishset === publishsetUrl) {
          found = true;
          return {
            ...publishset,
            required_formats: [...publishset.required_formats, formatUrl],
          };
        }
        return publishset;
      });
      if (!found) {
        order_publishsets = [
          ...order_publishsets,
          {
            publishset: publishsetUrl,
            required_formats: [formatUrl],
          },
        ];
      }

      return {
        ...state,
        isDirty: true,
        order_publishsets,
      };
    }

    case con.NEW_ORDER_PUBLISHSET_REMOVE_FORMAT: {
      const { publishsetUrl, formatUrl } = action.payload as PublishsetFormatPayload;

      const order_publishsets = state.order_publishsets.map((publishset) => {
        if (publishset.publishset === publishsetUrl) {
          const required_formats = publishset.required_formats.filter(
            (format) => format !== formatUrl,
          );
          return {
            ...publishset,
            required_formats,
          };
        }
        return publishset;
      });

      return {
        ...state,
        isDirty: true,
        order_publishsets: order_publishsets.filter(
          (order_publishset) => order_publishset.required_formats.length > 0,
        ),
      };
    }

    case con.NEW_ORDER_PUBLISHSET_ADD_ALL_FORMATS: {
      const { publishsetUrl, formatsUrl } = action.payload as PublishsetSetAllFormatsPayload;
      let found = false;
      let order_publishsets = state.order_publishsets.map((order_publishset) => {
        if (order_publishset.publishset === publishsetUrl) {
          found = true;
          return {
            ...order_publishset,
            required_formats: formatsUrl,
          };
        }
        return order_publishset;
      });
      if (!found) {
        order_publishsets = [
          ...order_publishsets,
          {
            publishset: publishsetUrl,
            required_formats: formatsUrl,
          },
        ];
      }

      return {
        ...state,
        isDirty: true,
        order_publishsets,
      };
    }

    case con.NEW_ORDER_PUBLISHSET_REMOVE_ALL_FORMATS: {
      const { publishsetUrl } = action.payload as PublishsetRemoveAllFormatsPayload;
      return {
        ...state,
        isDirty: true,
        order_publishsets: state.order_publishsets.filter(
          (order_publishset) => order_publishset.publishset !== publishsetUrl,
        ),
      };
    }

    case con.NEW_ORDER_SET_REASON:
      return {
        ...state,
        isDirty: true,
        reason: action.reason,
      };
    case con.NEW_ORDER_SET_HANDLING_FEE:
      return {
        ...state,
        isDirty: true,
        handling_fee: action.handling_fee,
      };
    case con.NEW_ORDER_SET_NOTE:
      return {
        ...state,
        isDirty: true,
        note: action.note,
      };
    case con.NEW_ORDER_SET_ORIGIN:
      return {
        ...state,
        isDirty: true,
        origin: action.origin,
      };
    case con.NEW_ORDER_SET_AGREEMENT:
      return {
        ...state,
        agreement: action.payload,
        isRequirementAgreement: state.isRequirementAgreement && !action.payload,
      };
    case con.NEW_ORDER_SET_QUERY_STATE:
      return {
        ...state,
        isDirty: action.queryState === QUERY_STATE.SUCCESS ? false : state.isDirty,
        fieldErrors: action.fieldErrors,
        queryState: action.queryState,
      };
    case con.NEW_ORDER_SET_ID:
      return {
        ...state,
        isDirty: true,
        id: action.id,
      };
    case con.NEW_ORDER_SET_IS_REQUIREMENT_DATE:
      return {
        ...state,
        isRequirementDate: action.payload,
      };

    case con.NEW_ORDER_SET_REQUIREMENTS:
      return {
        ...state,
        isDirty: true,
        isRequirementPublishset: action.isRequirementPublishset,
        isRequirementValidRegion: action.isRequirementValidRegion,
        isRequirementAgreement: action.isRequirementAgreement,
        isRequirementDate: action.isRequirementDate,
      };
    case con.NEW_ORDER_ADD_ATTACHMENT:
      return {
        ...state,
        isDirty: true,
        attachments: [...state.attachments, action.payload],
      };
    case con.NEW_ORDER_INIT_ATTACHMENTS: {
      const { payload } = action;
      return {
        ...state,
        isDirty: true,
        attachments: payload,
      };
    }
    case con.NEW_ORDER_DELETE_ATTACHMENT: {
      return {
        ...state,
        isDirty: true,
        attachments: state.attachments.filter(
          ({ luid, id }) => luid !== action.payload.key && id !== action.payload.key,
        ),
      };
    }
    case con.NEW_ORDER_SET_DATA_VALIDITY:
      return {
        ...state,
        isDirty: true,
        order_type: action.order_type,
        order_data_state_valid_at: null,
        order_data_changes_from: null,
        order_publishsets: [],
        isRequirementDate: false,
        dateValidityMin: null,
        dateValidityMax: null,
      };
    case con.NEW_ORDER_SET_DATA_VALIDITY_STATE_DATE:
      return {
        ...state,
        isDirty: true,
        order_data_state_valid_at: action.order_data_state_valid_at,
        // clear order_publishsets, only when previous order_data_state_valid_at is null
        order_publishsets: state.order_data_state_valid_at ? state.order_publishsets : [],
        dateValidityMin: action.dateValidityMin,
        dateValidityMax: action.dateValidityMax,
      };
    case con.NEW_ORDER_SET_DATA_VALIDITY_CHANGE_DATE_FROM:
      return {
        ...state,
        isDirty: true,
        order_data_changes_from: action.order_data_changes_from,
        dateValidityMin: action.dateValidityMin,
        dateValidityMax: action.dateValidityMax,
      };
    case con.NEW_ORDER_SET_SELECTION_TYPE:
      return {
        ...state,
        isDirty: true,
        selection_type: action.payload,
        order_data_state_valid_at: null,
        order_data_changes_from: null,
        order_publishsets: [],
        isRequirementDate: false,
        dateValidityMin: null,
        dateValidityMax: null,
      };
    case con.NEW_ORDER_SET_AREA_TYPE:
      return {
        ...state,
        areaType: action.payload,
      };
    case con.NEW_ORDER_SET_IS_PUBLIC:
      return {
        ...state,
        isDirty: true,
        order_publishsets: [],
        is_public: action.payload,
      };

    case con.NEW_ORDER_SET_OVER_LIMIT_AREA:
      return {
        ...state,
        isDirty: true,
        isOverLimitArea: action.payload,
      };

    case con.NEW_ORDER_SET_PRIMARY_DB:
      return {
        ...state,
        isDirty: true,
        order_publishsets: [],
        from_primary_db: action.payload,
      };

    default:
      return state;
  }
};
